import {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import type {MixRgb} from "@cm/material-nodes"

type Image = Nodes.ImageNode
type Rgb = readonly [number, number, number]
export type MixBlendType = NonNullable<MixRgb["parameters"]["settings"]["blendType"]>

/** Which of the mix node's two colours the chain image is; the other one is the constant. */
export type MixImageSide = "color1" | "color2"

// The modes are composed from `math` nodes because one side of the mix is always a constant in a
// linear chain. A mix whose second input is an image too (a texture-driven factor, two textures)
// does not reduce that way and would want a native mix node in @cm/image-processing instead.

const add = (x: Image, y: Image | number) => Nodes.math("+", x, y)
const sub = (x: Image, y: Image | number) => Nodes.math("-", x, y)
const mul = (x: Image, y: Image | number) => Nodes.math("*", x, y)
const safeDivide = (x: Image, y: Image | number) => Nodes.math("/safe", x, y)
const oneMinus = (x: Image) => add(mul(x, -1), 1)
const isZero = (x: Image) => Nodes.math("==", x, 0)
/** `(1 − t) x + t y`, Blender's `interp`. */
const interp = (x: Image, y: Image, t: number) => add(mul(x, 1 - t), mul(y, t))
/** `(1 − t) + t x`: the weight the multiplicative modes scale by. */
const weight = (x: Image, t: number) => add(mul(x, t), 1 - t)
/** `whenSet` where the 0/1 mask is 1, `otherwise` where it is 0. */
const select = (mask: Image, whenSet: Image, otherwise: Image) => add(mul(whenSet, mask), mul(otherwise, oneMinus(mask)))

const constantImage = (color: Rgb): Image => Nodes.createImage(1, 1, "float32", "linear", [color[0], color[1], color[2]])

/**
 * One arithmetic blend mode of Blender's mix node: `a` is Color1, `b` is Color2, `t` the factor.
 * Either side may be the chain image and the other a 1×1 constant; a builder must not assume which.
 */
type ArithmeticMode = (a: Image, b: Image, t: number) => Image

const screen: ArithmeticMode = (a, b, t) => oneMinus(mul(weight(oneMinus(b), t), oneMinus(a)))

const softLight: ArithmeticMode = (a, b, t) => {
    const scr = oneMinus(mul(oneMinus(b), oneMinus(a)))
    return interp(a, add(mul(mul(oneMinus(a), b), a), mul(a, scr)), t)
}

const divide: ArithmeticMode = (a, b, t) => select(isZero(b), a, interp(a, safeDivide(a, b), t))

const overlay: ArithmeticMode = (a, b, t) => {
    const dark = mul(a, weight(mul(b, 2), t))
    const light = oneMinus(mul(weight(mul(oneMinus(b), 2), t), oneMinus(a)))
    return select(Nodes.math("<", a, 0.5), dark, light)
}

const dodge: ArithmeticMode = (a, b, t) => {
    const divisor = oneMinus(mul(b, t))
    const divisorIsSpent = Nodes.math("<=", divisor, 0)
    const dodged = Nodes.math("min", safeDivide(a, divisor), 1)
    // 1 where the divisor is spent, the capped quotient elsewhere; a channel at exactly 0 stays 0.
    return mul(add(mul(dodged, oneMinus(divisorIsSpent)), divisorIsSpent), oneMinus(isZero(a)))
}

const burn: ArithmeticMode = (a, b, t) => {
    const divisor = weight(b, t)
    const burned = Nodes.math("clip01", oneMinus(safeDivide(oneMinus(a), divisor)))
    return mul(burned, oneMinus(Nodes.math("<=", divisor, 0)))
}

const ARITHMETIC_MODES = {
    MIX: (a, b, t) => interp(a, b, t),
    ADD: (a, b, t) => add(a, mul(b, t)),
    SUBTRACT: (a, b, t) => sub(a, mul(b, t)),
    MULTIPLY: (a, b, t) => mul(a, weight(b, t)),
    SCREEN: screen,
    DIFFERENCE: (a, b, t) => interp(a, Nodes.math("abs", sub(a, b)), t),
    DARKEN: (a, b, t) => interp(a, Nodes.math("min", a, b), t),
    LIGHTEN: (a, b, t) => interp(a, Nodes.math("max", a, b), t),
    LINEAR_LIGHT: (a, b, t) => add(a, mul(add(mul(b, 2), -1), t)),
    EXCLUSION: (a, b, t) => Nodes.math("max", interp(a, sub(add(a, b), mul(mul(a, b), 2)), t), 0),
    SOFT_LIGHT: softLight,
    DIVIDE: divide,
    OVERLAY: overlay,
    DODGE: dodge,
    BURN: burn,
} satisfies Partial<Record<MixBlendType, ArithmeticMode>>

/*
 * The HSV modes. Blender converts both colours to HSV, replaces part of Color1's triple and converts
 * back. None of that needs a hue angle here, because of two facts about its `rgb_to_hsv` and
 * `hsv_to_rgb` (intern/cycles/util/color.h):
 *
 *   value = max, saturation = (max − min) / max, and 0 where max is 0;
 *   hsv_to_rgb(h, s, v) = v · ((1 − s) + s · base(h)) per channel, also for s = 0,
 *
 * where base(h) is the fully saturated colour of that hue. For a colour x it is
 * (x − min) / (max − min), since converting x there and back returns x. So "the hue of x" is carried
 * as base(x) and never as an angle.
 *
 * One side is a constant, whose parts are plain numbers; the other is the image, whose parts are
 * images. A per-pixel scalar is spread to three channels at once, because the math operation cannot
 * combine a 1×1 colour constant with a single-channel image.
 */

/** The HSV parts of the chain image, each as a three-channel image. */
const imageHsv = (image: Image) => {
    const channels = [0, 1, 2].map((channel) => Nodes.extractChannel(image, channel))
    const spread = (scalar: Image): Image => Nodes.combineChannels([scalar, scalar, scalar], "RGB")
    const max = spread(Nodes.math("max", Nodes.math("max", channels[0], channels[1]), channels[2]))
    const min = spread(Nodes.math("min", Nodes.math("min", channels[0], channels[1]), channels[2]))
    const delta = sub(max, min)
    // `/safe` yields 0 for a zero divisor, which is Blender's saturation of black and the base of a grey.
    return {max, min, delta, saturation: safeDivide(delta, max), base: safeDivide(sub(image, min), delta)}
}

/** The HSV parts of the constant colour, as numbers. */
const constantHsv = (color: Rgb) => {
    const max = Math.max(...color)
    const min = Math.min(...color)
    const delta = max - min
    const saturation = max !== 0 ? delta / max : 0
    const base: Rgb = delta !== 0 ? [(color[0] - min) / delta, (color[1] - min) / delta, (color[2] - min) / delta] : [0, 0, 0]
    // hsv_to_rgb(h, s, 1) of this colour: what a value is multiplied by to give it this hue and saturation.
    const tint: Rgb = [1 - saturation + saturation * base[0], 1 - saturation + saturation * base[1], 1 - saturation + saturation * base[2]]
    return {max, min, delta, saturation, base, tint}
}

/** hsv_to_rgb(h, s, 1) of the image, per pixel. */
const imageTint = ({saturation, base}: ReturnType<typeof imageHsv>) => add(oneMinus(saturation), mul(saturation, base))

/** The constant as an image the size of `like`, for a result that is the constant untouched. */
const constantLike = (like: Image, color: Rgb): Image => Nodes.math("constLike", like, constantImage(color))

type HsvMode = (image: Image, constant: Rgb, imageSide: MixImageSide, t: number) => Image

// Color1 keeps its saturation and value and takes Color2's hue, unless Color2 is a grey.
const hue: HsvMode = (image, constant, imageSide, t) => {
    const c = constantHsv(constant)
    if (imageSide === "color1") {
        if (c.saturation === 0) return image
        const a = imageHsv(image)
        return interp(image, add(a.min, mul(a.delta, constantImage(c.base))), t)
    }
    const b = imageHsv(image)
    const a = constantImage(constant)
    return select(isZero(b.saturation), a, add(mul(add(mul(b.base, c.delta), c.min), t), mul(a, 1 - t)))
}

// Color1 keeps its hue and value and moves its saturation towards Color2's, unless Color1 is a grey.
const saturation: HsvMode = (image, constant, imageSide, t) => {
    const c = constantHsv(constant)
    if (imageSide === "color1") {
        const a = imageHsv(image)
        const mixed = add(mul(a.saturation, 1 - t), t * c.saturation)
        return select(isZero(a.saturation), image, mul(a.max, add(oneMinus(mixed), mul(mixed, a.base))))
    }
    if (c.saturation === 0) return constantLike(image, constant)
    const mixed = add(mul(imageHsv(image).saturation, t), (1 - t) * c.saturation)
    return add(mul(oneMinus(mixed), c.max), mul(mul(mixed, c.max), constantImage(c.base)))
}

// Color1 keeps its hue and saturation and moves its value towards Color2's.
const value: HsvMode = (image, constant, imageSide, t) => {
    const c = constantHsv(constant)
    if (imageSide === "color1") {
        const a = imageHsv(image)
        return mul(add(mul(a.max, 1 - t), t * c.max), imageTint(a))
    }
    return mul(add(mul(imageHsv(image).max, t), (1 - t) * c.max), constantImage(c.tint))
}

// Color1 keeps its value and takes Color2's hue and saturation, unless Color2 is a grey.
const color: HsvMode = (image, constant, imageSide, t) => {
    const c = constantHsv(constant)
    if (imageSide === "color1") {
        if (c.saturation === 0) return image
        return interp(image, mul(imageHsv(image).max, constantImage(c.tint)), t)
    }
    const b = imageHsv(image)
    const a = constantImage(constant)
    return select(isZero(b.saturation), a, add(mul(mul(imageTint(b), c.max), t), mul(a, 1 - t)))
}

const HSV_MODES = {HUE: hue, SATURATION: saturation, VALUE: value, COLOR: color} satisfies Partial<Record<MixBlendType, HsvMode>>

const arithmeticAsMode =
    (mode: ArithmeticMode): HsvMode =>
    (image, constant, imageSide, t) =>
        imageSide === "color1" ? mode(image, constantImage(constant), t) : mode(constantImage(constant), image, t)

/**
 * Every blend mode of Blender's mix node, each a transcription of its `svm_mix_*` function in
 * `intern/cycles/kernel/svm/color_util.h`, for one colour that is an image and one that is a constant.
 */
const MIX_MODES: Record<MixBlendType, HsvMode> = {
    ...(Object.fromEntries(Object.entries(ARITHMETIC_MODES).map(([blendType, mode]) => [blendType, arithmeticAsMode(mode)])) as Record<
        keyof typeof ARITHMETIC_MODES,
        HsvMode
    >),
    ...HSV_MODES,
}

/**
 * The mix of the chain image with a constant colour. `image` must be three-channel linear float; `t`
 * is the factor, already clamped. The result has the image's size in every case.
 */
export const mixImageWithConstant = (blendType: MixBlendType, image: Image, constant: Rgb, imageSide: MixImageSide, t: number): Image =>
    MIX_MODES[blendType](image, constant, imageSide, t)
